'use client'

import { useMemo } from 'react'
import { ContentfulLivePreview } from '@contentful/live-preview'
import { documentToReactComponents } from '@contentful/rich-text-react-renderer'

import { Container } from '../../components/container'
import { SmartImage } from '../../components/smart-image'
import { useBreakpoints } from '../../hooks/use-breakpoints'
import { CmsColor } from '../../models/cms-color'
import { cn } from '../../utils/cn'
import { a11yContrastShade, hexToRgbaCSS } from '../../utils/color-helpers'
import { ButtonCms, ButtonCmsModel } from '../button/button.cms'
import {
  BackgroundShapeType,
  CmsBackgroundShape,
} from '../image-shape/background-shape'
import {
  ImageShape,
  ImageShapeSize,
  ImageShapeType,
} from '../image-shape/image-shape'

interface HeroCmsProps {
  isH1?: boolean
  id: string
  title: any | string
  description?: any | string
  button?: ButtonCmsModel
  img?: {
    alt: string
    href: string
    pixelatedUrl?: string
  }
  align: 'right' | 'left' | 'center'
  imageShape?: ImageShapeType
  backgroundShape?: BackgroundShapeType
  color?: CmsColor
  eyebrow?: any | string
}

const HeroCms = (props: HeroCmsProps) => {
  const alignClass = useMemo(() => {
    switch (props.align) {
      case 'center':
        return 'justify-center md:flex-row'
      case 'left':
        return 'justify-start md:flex-row'
      case 'right':
        return 'justify-start flex-col-reverse md:flex-row-reverse'
      default:
        return
    }
  }, [props.align])

  const isBackgroundImage = useMemo(() => !!props.img?.href, [props.img])

  const breakpoints = useBreakpoints()

  /** If the hero has a background image, we need to make sure that the text is visible no matter the color of
   * that image. This is used to apply a background color beneath the text that will be accessible to the chosen
   * text color (creates enough contrast)
   */
  const a11yTextBackgroundColor = useMemo(
    () =>
      isBackgroundImage
        ? hexToRgbaCSS(
            a11yContrastShade(props.color?.textColor || '#000000'),
            0.7
          )
        : null,
    [isBackgroundImage]
  )

  const textAlignClass = useMemo(() => {
    switch (props.align) {
      case 'center':
        return 'text-center'
      case 'left':
        return 'text-left'
      case 'right':
        return 'text-left'
      default:
        return
    }
  }, [props.align])

  const isImageShape = useMemo(
    () => props.imageShape && props.imageShape.shapeName,
    [props.imageShape]
  )

  const minHeight = useMemo(() => {
    if (breakpoints.isMobile) {
      return 'unset'
    }
    if (!isImageShape) {
      return '23.87rem'
    }
    if (props.imageShape?.size === ImageShapeSize.Small) {
      return breakpoints.isTablet ? '18rem' : '22.46rem'
    }
    if (breakpoints.isTablet) {
      return props.isH1 ? 'min(30rem, 45vw)' : 'min(25rem, 40vw)'
    }
    return props.isH1 ? 'min(33rem, 36vw)' : 'min(30rem, 36vw)'
  }, [props.isH1, isImageShape, breakpoints])

  const title = () =>
    !props.title || typeof props.title === 'string'
      ? props.title
      : documentToReactComponents(props.title?.json)

  const description = () =>
    !props.description || typeof props.description === 'string'
      ? props.description
      : documentToReactComponents(props.description.json)

  let textClasses = ''
  let styles: any = {}
  if (props.isH1) {
    textClasses = 'leading-[110%]'
    styles.fontSize = 'max(min(5rem, 5vw), 3rem)'
  } else if (props.title && !props.description?.json) {
    textClasses = 'text-5xl sm:text-4xl lg:text-5xl'
  } else {
    textClasses = 'leading-[110%]'
    styles.fontSize = 'max(min(4rem, 4vw), 2rem)'
  }

  return (
    <div id={`${props.id}`} className="scroll-mt-25">
      <SmartImage
        isBackgroundImg={true}
        src={props.img?.href}
        pixelatedUrl={props.img?.pixelatedUrl}
        data-testid="background"
        style={{
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          position: 'relative',
          overflow: 'hidden',
          backgroundColor: props.color?.backgroundColor || '',
          color: props.color?.textColor || '',
          minHeight: minHeight,
        }}
        role={props.img ? 'img' : ''}
        alt={props.img ? props.img.alt : ''}
        {...ContentfulLivePreview.getProps({
          entryId: props.id,
          fieldId: 'image',
          locale: 'en-US',
        })}
        className={cn(isImageShape ? `` : 'flex  flex-col justify-center')}
      >
        {props.backgroundShape?.shapeName && (
          <CmsBackgroundShape
            content={props.backgroundShape as any}
            color={props.color?.backgroundColor || 'slategray'}
          />
        )}

        <Container
          className={cn(
            props.imageShape?.size !== ImageShapeSize.Full ? 'py-6' : '',
            !props.imageShape?.imageUrl ? 'flex  flex-col' : '',
            'z-2 relative h-full'
          )}
        >
          <section
            data-testid="hero-body"
            className={cn(
              'flex h-full flex-1 flex-col items-center gap-6 lg:gap-24',
              alignClass,
              props.align === 'right' && 'mt-12 sm:mt-0'
            )}
          >
            <div
              className={cn(
                'my-12',
                props.imageShape?.shapeName
                  ? props.imageShape?.size === ImageShapeSize.Full
                    ? 'flex-1'
                    : 'flex-[2]'
                  : cn(textAlignClass),
                ' flex flex-col justify-center'
              )}
              style={
                isBackgroundImage
                  ? {
                      backgroundColor: a11yTextBackgroundColor as string,
                      backdropFilter: 'blur(10px)',
                      padding: '1rem',
                      borderRadius: '8px',
                    }
                  : {}
              }
            >
              {props.eyebrow && (
                <div className="flex justify-center">
                  <div className="w-auto px-3 py-1 mb-4 font-sans text-sm font-medium text-white rounded-sm bg-primary-500">
                    {props.eyebrow}
                  </div>
                </div>
              )}
              <h2
                aria-level={props.isH1 ? 1 : 2}
                className={cn('mb-1', textClasses)}
                style={{
                  ...styles,
                  color: props.color?.textColor || '',
                }}
                {...ContentfulLivePreview.getProps({
                  entryId: props.id,
                  fieldId: 'title',
                  locale: 'en-US',
                })}
              >
                {title()}
              </h2>

              <div
                {...ContentfulLivePreview.getProps({
                  entryId: props.id,
                  fieldId: 'description',
                  locale: 'en-US',
                })}
              >
                {props.description && (
                  <div
                    className={cn(
                      'mt-4 lg:mt-6 max-w-[51rem] mx-auto',
                      isImageShape
                        ? 'text-base lg:text-2xl'
                        : 'text-base lg:text-lg xl:text-xl'
                    )}
                    style={{ color: props.color?.textColor || '' }}
                  >
                    {description()}
                  </div>
                )}
              </div>

              {(props.button?.slug || props.button?.externalLink) && (
                <div
                  className="mt-6"
                  {...ContentfulLivePreview.getProps({
                    entryId: props.id,
                    fieldId: 'button',
                    locale: 'en-US',
                  })}
                >
                  <ButtonCms
                    content={props.button}
                    backgroundColor={props.color?.backgroundColor}
                    className="w-full sm:w-auto"
                  />
                </div>
              )}
            </div>

            {isImageShape && (
              <div
                style={
                  breakpoints.isMobile
                    ? {
                        width: '100%',
                      }
                    : {
                        height: minHeight,
                      }
                }
              >
                <ImageShape
                  {...ContentfulLivePreview.getProps({
                    entryId: props.id,
                    fieldId: 'imageShape',
                    locale: 'en-US',
                  })}
                  content={props.imageShape as any}
                />
              </div>
            )}
          </section>
        </Container>
      </SmartImage>
    </div>
  )
}

export { HeroCms }
